import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';


const goalCardStyles = `
  :root {
    --accent: #73abff;
    --border: #e2e8f0;
    --text-main: #1e293b;
    --text-sub: #64748b;
  }

  .goal-card {
    position: relative;
    /* Increased width to 850px for a 'Wide' dashboard feel */
    max-width: 500px; 
    width: 100%;
    margin: 0 auto 24px auto;
    background: white;
    border: 1px solid #ddd;
    border-radius: 14px;
    /* Larger padding for a more premium look */
    padding: 28px 32px !important; 
    border-left: 10px solid var(--accent) !important;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .goal-card:hover {
    transform: translateY(-2px) translateX(4px);
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  }

  .goal-card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 22px;
  }

  .goal-title-text {
    font-size: 1.6rem; /* Slightly larger */
    font-weight: 800;
    color: var(--text-main);
    margin: 0;
    letter-spacing: -0.025em;
  }

  .difficulty-tag {
    font-size: 0.8rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    margin-top: 6px;
    display: inline-block;
  }

  .goal-card-actions {
    display: flex;
    gap: 16px;
    margin-top: 15px;
  }

  .view-details-btn, .mark-done-btn {
    padding: 14px 28px;
    font-size: 1rem;
    font-weight: 700;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .view-details-btn {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    color: var(--text-sub);
  }

  .view-details-btn:hover {
    background: #f1f5f9;
    color: var(--text-main);
  }

  .mark-done-btn {
    border: none;
    color: white;
    background-color: var(--accent);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .mark-done-btn:hover {
    filter: brightness(1.1);
    transform: translateY(-1px);
  }

  .control-icon-wrapper {
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: #94a3b8;
    cursor: pointer;
    transition: 0.2s;
  }

  .control-icon-wrapper:hover {
    background: #f1f5f9;
    color: var(--accent);
  }
`;
// --- MOCK COMPONENTS (Placeholders for your specific dropdowns/modals) ---
const NotificationDropdown = () => <div className="mock-drop">🔔</div>;
const ProfileDropdown = ({ user, onLogout }) => (
  <div className="mock-drop" onClick={onLogout}>👤 {user?.username} (Logout)</div>
);
const SocialModal = () => null;
const GoalCategoryTag = ({ category }) => <span className="cat-tag">{category || 'General'}</span>;
const GoalCategoryPickerModal = () => null;
const UserEntryList = ({ entries }) => (
  <div className="entry-list">{entries?.map((e, i) => <p key={i}>• {e.description}</p>)}</div>
);
const UserAddEntryForm = () => <div className="mock-form">Add Note...</div>;
const UserGoalCheckCalendar = () => <div className="mock-cal">Calendar View</div>;

// --- 1. USER RANK BADGE ---
const RANK_IMAGES = {
  // Add your local paths here if needed
  BRONZE: null, SILVER: null, GOLD: null, IRON: null, 
  EMERALD: null, PLATINUM: null, DIAMOND: null,
};

function UserRankBadge({ rank, customStyle, className }) {
  const defaultStyle = { width: '160px', height: '130px', objectFit: 'contain', display: 'block', margin: '0 auto' };
  const lookupRank = rank ? rank.trim().toUpperCase() : '';
  const emblemSrc = RANK_IMAGES[lookupRank];

  return (
    <div className={`rank-badge-container ${className || ''}`}>
      {emblemSrc ? (
        <img src={emblemSrc} alt={`${rank} rank`} style={customStyle || defaultStyle} />
      ) : (
        <div style={{ fontSize: '3rem', textAlign:'center' }}>🏆</div>
      )}
    </div>
  );
}

// --- 2. RANK PANEL ---
const RANKS = ['UNRANKED', 'IRON', 'BRONZE', 'SILVER', 'GOLD', 'PLATINUM', 'EMERALD', 'DIAMOND'];

function RankPanel({ progress }) {
  if (!progress) return <div>No progress found.</div>;
  const currentIndex = RANKS.indexOf(progress.currentRank);
  const nextRank = currentIndex < RANKS.length - 1 ? RANKS[currentIndex + 1] : 'MAX LEVEL';

  return (
    <div className="glass-card rank-panel" style={{ display: 'flex', gap: '20px', padding: '20px', alignItems: 'center' }}>
      <div style={{ textAlign: 'center', borderRight: '1px solid var(--border)', paddingRight: '20px' }}>
        <UserRankBadge rank={progress.currentRank} />
        <div style={{ fontWeight: '800', fontSize: '1.2rem', color: 'var(--primary)' }}>{progress.currentRank}</div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-sub)' }}>{progress.totalXP} Total XP</div>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ fontWeight: '600' }}>Daily Focus</span>
          <span style={{ color: 'var(--text-sub)' }}>{progress.dailyXP} / 250 XP</span>
        </div>
        <div className="daily-focus-bar" style={{ background: '#eee', height: '10px', borderRadius: '5px', overflow: 'hidden' }}>
          <div style={{ width: `${(progress.dailyXP / 250) * 100}%`, background: 'var(--primary, #4ade80)', height: '100%' }} />
        </div>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-sub)', marginTop: '12px' }}>
          Next Rank: <b>{nextRank}</b> • {250 - (progress.dailyXP || 0)} XP to go today
        </p>
      </div>
    </div>
  );
}

// --- 3. ACTIVITY HEATMAP ---
function UserGlobalYearCalendar({ contributions = [] }) {
  const getColor = (count) => {
    if (count <= 0) return '#e5e7eb';
    if (count === 1) return '#bbf7d0';
    if (count >= 5) return '#16a34a';
    return '#48e57fff';
  };

  return (
    <div className="glass-card" style={{ padding: '20px', marginBottom: '32px' }}>
      <h4 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', color: 'var(--text-sub)' }}>Activity - Last 12 Months</h4>
      <div style={{ display: 'flex', gap: '2px', overflowX: 'auto' }}>
        {Array.from({ length: 52 }).map((_, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {Array.from({ length: 7 }).map((_, j) => (
              <div key={j} style={{ width: '12px', height: '12px', borderRadius: '2px', backgroundColor: getColor(Math.floor(Math.random() * 6)) }} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}


function UserGoalCard({ goal }) {
  const [isOpen, setIsOpen] = useState(false);
  
  const diffStyles = {
    1: { label: 'Easy', color: '#73abff' },
    2: { label: 'Medium', color: '#ffca6e' },
    3: { label: 'Hard', color: '#fe6262' },
  };
  const currentDiff = diffStyles[goal.difficulty] || diffStyles[1];

  return (
    <div className="goal-card" style={{ '--accent': currentDiff.color }}>
      <style>{goalCardStyles}</style>
      
      <div className="goal-card-header">
        <div className="goal-info">
          <div style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#94a3b8', marginBottom: '4px' }}>
            {goal.category?.toUpperCase() || 'GENERAL'}
          </div>
          <h3 className="goal-title-text">{goal.title}</h3>
          <span className="difficulty-tag" style={{ color: currentDiff.color }}>
            {currentDiff.label}
          </span>
        </div>

        <div className="goal-controls">
          <button className="control-icon-wrapper">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="12" cy="12" r="1" /><circle cx="12" cy="5" r="1" /><circle cx="12" cy="19" r="1" />
            </svg>
          </button>
        </div>
      </div>

      <div className="goal-card-actions">
        <button className="view-details-btn" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? 'Close Details' : 'View Progress'}
        </button>
        <button className="mark-done-btn" style={{ backgroundColor: currentDiff.color }}>
          Mark Done
        </button>
      </div>

      {isOpen && (
        <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #f1f5f9' }}>
           {/* Your Calendar/Entry components go here */}
           <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '10px', textAlign: 'center', color: '#64748b' }}>
             Expanded Tracker View
           </div>
        </div>
      )}
    </div>
  );
}



// --- 5. TOP NAVIGATION ---
function TopNav({ currentUser }) {
  const location = useLocation();
  const isActive = (path) => location.pathname === path;
  if (!currentUser) return null;

  return (
    <nav className="top-nav" style={{ display: 'flex', justifyContent: 'space-between', padding: '1rem', background: '#fff', borderBottom: '1px solid #eee' }}>
      <div className="nav-links-container" style={{ display: 'flex', gap: '15px' }}>
        {['Home', 'Goals', 'Leaderboard', 'Archive'].map((link) => (
           <Link key={link} to={`/${link}`} className={`nav-link ${isActive(`/${link}`) ? 'active' : ''}`} style={{ textDecoration: 'none', color: '#333' }}>
             {link}
           </Link>
        ))}
      </div>
      <div className="nav-actions-right" style={{ display: 'flex', gap: '15px' }}>
        <NotificationDropdown />
        <ProfileDropdown user={currentUser} />
      </div>
    </nav>
  );
}

// --- MAIN COMBINED PAGE COMPONENT ---
export default function UnifiedProfilePage() {
  // Mock State for "The Looks"
  const [currentUser] = useState({ id: 1, username: 'User123' });
  const [profile] = useState({
    firstName: 'John',
    lastName: 'Doe',
    followersCount: 120,
    followingCount: 85,
    rankProgress: { currentRank: 'GOLD', totalXP: 1250, dailyXP: 180 },
    goals: [
      { id: 1, title: 'Morning Run', difficulty: 2, category: 'Fitness', entries: [] },
      { id: 2, title: 'Read 20 Pages', difficulty: 1, category: 'Learning', entries: [] }
    ]
  });

  return (
    <div className="app-container" style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>
      <TopNav currentUser={currentUser} />

      <header className="profile-view-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '40px 0' }}>
        <div className="profile-info-stack">
          <h1 className="profile-name-text" style={{ fontSize: '2.5rem', margin: 0 }}>
            {profile.firstName} {profile.lastName}
          </h1>
          <div className="stats-mini" style={{ display: 'flex', gap: '15px', marginTop: '10px' }}>
            <span><strong>{profile.followersCount}</strong> Followers</span>
            <span><strong>{profile.followingCount}</strong> Following</span>
          </div>
        </div>
        <div className="profile-header-actions">
          <button style={{ fontSize: '1.5rem', background: 'none', border: '1px solid #ddd', borderRadius: '8px', padding: '5px 10px' }}>🏆</button>
        </div>
      </header>

      <RankPanel progress={profile.rankProgress} />

      <h3 style={{ fontSize: '1.1rem', margin: '30px 0 12px' }}>Activity Heatmap</h3>
      <UserGlobalYearCalendar />

      <h3 style={{ fontSize: '1.1rem', margin: '30px 0 12px' }}>Public Goals</h3>
      <div className="goal-list">
        {profile.goals.map(goal => (
          <UserGoalCard key={goal.id} goal={goal} isReadOnly={true} isDoneToday={false} />
        ))}
      </div>

      <SocialModal isOpen={false} />
    </div>
  );
}